// The organization's brand as the device uses it (D-83, D-174).
//
// One bundle serves every tenant: the name, the three colours and the three
// images arrive at runtime, from the profile once signed in and from
// bootstrap before that. The images are served by public routes of the API,
// by host like bootstrap itself, so they work on the access screen too.
//
// The foreground over each colour is chosen by contrast (contrast.ts) and
// written beside it as a token, so no component decides it and a customer's
// colour is never refused.
//
// INC-12a (D-309): the icon of the installed application joins the logo and
// the square mark. The icon of the tab and of the home screen follows one
// fallback order, the manifest's own: the icon uploaded; without one, the
// square mark; without either, the square of the organization's initial over
// its primary, which the API draws. Nothing of the product's is ever the icon
// of a customer's application.
//
// INC-14a-2a (D-371): a logo for dark backgrounds may join them. Where the logo
// goes over a brand colour with no plate under it — the bar of the field, the
// menu of the office — it is drawn instead of the logo when the text the
// contrast rule chooses over that colour is the light one (`logoOver`).
//
// INC-16 (D-421): and the organization's white label, from the same readings:
// on, nothing the device shows names the product (i18n/index.ts).
//
// INC-16.1 (D-424): the profile names the organization while the session it
// was read with is open, and is read again on its own (session/manager.ts).
// A session the server ended keeps its profile on the device, which the
// field's runtime still runs on (fieldRuntime.ts), but the screens outside a
// session paint the organization the host's bootstrap read last: the access
// screen shows it as it is now, not as it was when that session began.
import type { MeResponse } from '../api/types';
import { foregroundOver, LIGHT_FOREGROUND } from '../ui/contrast';
import { isOpen, type SessionSnapshot } from '../session/manager';

export const LOGO_PATH = '/api/v1/app/logo';
export const MARK_PATH = '/api/v1/app/mark';
export const ICON_PATH = '/api/v1/app/icon';
/** INC-14a-2a (D-371): the logo for dark backgrounds. */
export const LOGO_DARK_PATH = '/api/v1/app/logo-dark';
/** The square of the initial, drawn by the API for the organization of the host. */
export const INITIAL_PATH = '/api/v1/app/initial.svg';

export interface Brand {
  name: string;
  primary: string | null;
  secondary: string | null;
  accent: string | null;
  /** The wide logo, when the organization has one in object storage. */
  logoUrl: string | null;
  /** The square mark: shown with the name where there is no logo, and the
   *  installed application's icon where there is no icon. */
  markUrl: string | null;
  /** The icon of the installed application and of the tab, when uploaded. */
  iconUrl: string | null;
  /** The logo for dark backgrounds, when the organization has one (D-371). */
  logoDarkUrl: string | null;
  /** INC-16 (D-421): whether the organization's people read the product's name. */
  whiteLabel: boolean;
}

export const NEUTRAL_BRAND: Brand = {
  name: '',
  primary: null,
  secondary: null,
  accent: null,
  logoUrl: null,
  markUrl: null,
  iconUrl: null,
  logoDarkUrl: null,
  whiteLabel: false,
};

/** INC-16.1 (D-424): the profile, while the session it was read with is open. */
export function openProfile(snapshot: SessionSnapshot): MeResponse | null {
  return isOpen(snapshot.status) ? snapshot.profile : null;
}

/** The brand of the signed-in organization, or of the one bootstrap named. */
export function brandOf(snapshot: SessionSnapshot): Brand {
  const source = openProfile(snapshot)?.organization ?? snapshot.bootstrap?.organization ?? null;
  if (source === null) return NEUTRAL_BRAND;
  return {
    name: source.name,
    primary: source.brand_primary,
    secondary: source.brand_secondary,
    accent: source.brand_accent,
    logoUrl: source.logo_key === null ? null : LOGO_PATH,
    markUrl: source.logo_mark_key === null ? null : MARK_PATH,
    iconUrl: source.app_icon_key === null ? null : ICON_PATH,
    // A profile stored by the release before names no such member: no such logo.
    logoDarkUrl: (source.logo_dark_key ?? null) === null ? null : LOGO_DARK_PATH,
    // INC-16: a reading stored by the release before names no such member: off
    // until the profile is read again, which the next start does with signal
    // (INC-16.1). Its type says it is always there; a stored one may not be.
    whiteLabel: (source as { white_label?: boolean }).white_label ?? false,
  };
}

/** The logo over a colour of the brand with no plate under it (D-371): the one
 *  for dark backgrounds when the text the contrast rule chooses over that colour
 *  is the light one and the organization has it; the logo otherwise, with its
 *  own fallback order. A colour the organization did not set is the layer's
 *  default, dark, which the light text reads over. */
export function logoOver(brand: Brand, colour: string | null): string | null {
  const light = foregroundOver(colour) === LIGHT_FOREGROUND;
  return light && brand.logoDarkUrl !== null ? brand.logoDarkUrl : brand.logoUrl;
}

/** The icon of the tab and of the home screen, by the fallback order of the
 *  manifest: the icon, the mark, the square of the initial. None without an
 *  organization: the neutral screen of an unknown host carries no icon. */
export function tabIconOf(brand: Brand): string | null {
  if (brand.name === '') return null;
  return brand.iconUrl ?? brand.markUrl ?? INITIAL_PATH;
}

/** Write the brand layer onto the root: the three colours and, beside each,
 *  the foreground that reads over it. A colour the organization did not set
 *  removes its token, and the layer's own default takes over. */
export function applyBrand(root: CSSStyleDeclaration, brand: Brand): void {
  const pair = (name: string, value: string | null): void => {
    if (value === null || value === '') {
      root.removeProperty(`--brand-${name}`);
      root.removeProperty(`--brand-on-${name}`);
      return;
    }
    root.setProperty(`--brand-${name}`, value);
    root.setProperty(`--brand-on-${name}`, foregroundOver(value));
  };
  pair('primary', brand.primary);
  pair('secondary', brand.secondary);
  pair('accent', brand.accent);
}

/** What the head of the document says of the organization: the icon of the
 *  tab, the icon and the name of the home screen of an iPad or an iPhone, the
 *  colour of the browser's own chrome. Each is the organization's or absent,
 *  never the product's; what a system cannot use, it replaces with its own. */
export function applyHead(head: HTMLHeadElement, brand: Brand): void {
  const icon = tabIconOf(brand);
  const link = (rel: string, href: string | null): void => {
    const existing = head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (href === null) {
      existing?.remove();
      return;
    }
    const element = existing ?? head.appendChild(head.ownerDocument.createElement('link'));
    element.rel = rel;
    if (element.getAttribute('href') !== href) element.href = href;
  };
  const meta = (name: string, content: string | null): void => {
    const existing = head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (content === null) {
      existing?.remove();
      return;
    }
    const element = existing ?? head.appendChild(head.ownerDocument.createElement('meta'));
    element.name = name;
    element.content = content;
  };
  link('icon', icon);
  link('apple-touch-icon', icon);
  meta('apple-mobile-web-app-title', brand.name === '' ? null : brand.name);
  meta('theme-color', brand.primary);
}
